// Broiler WK4 / WK6 weigh-in status for the batch cards (Ronnie's note 4,
// 2026-10-06; Ronnie's window answers 2026-10-08).
//
// The due date and the "weighed" test are tasks-cron's, read rather than
// shared (supabase/functions/tasks-cron/index.ts, collectBroilerEvents and
// hasStampedBroilerWeight): due = hatch date + 28 or + 42 days, and a week
// counts as weighed once week4Lbs / week6Lbs is above 0.
//
// The windows follow constitution Table D (C21) and Q10, with Ronnie's cut-off:
//   7 to 1 days before the date  -> warn   'WK4 Due In 5 Days'
//   on the date                  -> danger 'WK4 Due'
//   1 to 7 days after the date   -> danger 'WK4 Missed'
//   more than 7 days after       -> nothing (missed, no longer flagged)
// Because WK4 and WK6 are 14 days apart, the two red windows never overlap, so
// a card's one badge (Q15, worst-of) never has to hide a second red flag.

export const BROILER_WEIGH_IN_WEEKS = [
  {week: 4, days: 28, field: 'week4Lbs'},
  {week: 6, days: 42, field: 'week6Lbs'},
] as const;

// Days before the date that the amber "due soon" badge starts.
export const BROILER_WEIGH_IN_SOON_DAYS = 7;
// Days after the date that the red "missed" badge stays up.
export const BROILER_WEIGH_IN_MISSED_DAYS = 7;

export type BroilerWeighInBatch = {
  hatchDate?: string | null;
  hatch_date?: string | null;
  week4Lbs?: number | string | null;
  week6Lbs?: number | string | null;
  [key: string]: unknown;
};

export type BroilerWeighInFlag = {
  week: 4 | 6;
  state: 'soon' | 'due' | 'missed';
  tone: 'warn' | 'danger';
  dueDate: string;
  // Calendar days from today to the due date (negative once passed).
  daysUntil: number;
  label: string;
};

function isoDay(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const s = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function dayNumber(iso: string): number {
  return Math.round(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000);
}

function isoFromDayNumber(n: number): string {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

export function broilerWeighInFlags(
  batch: BroilerWeighInBatch | null | undefined,
  todayIso: string,
): BroilerWeighInFlag[] {
  const hatch = isoDay(batch?.hatchDate) || isoDay(batch?.hatch_date);
  const today = isoDay(todayIso);
  if (!batch || !hatch || !today) return [];
  const flags: BroilerWeighInFlag[] = [];
  for (const w of BROILER_WEIGH_IN_WEEKS) {
    const weighed = Number(batch[w.field]) > 0;
    if (weighed) continue;
    const due = dayNumber(hatch) + w.days;
    const daysUntil = due - dayNumber(today);
    const dueDate = isoFromDayNumber(due);
    const prefix = 'WK' + w.week;
    if (daysUntil > BROILER_WEIGH_IN_SOON_DAYS || daysUntil < -BROILER_WEIGH_IN_MISSED_DAYS) continue;
    if (daysUntil > 0) {
      const unit = daysUntil === 1 ? 'Day' : 'Days';
      flags.push({
        week: w.week,
        state: 'soon',
        tone: 'warn',
        dueDate,
        daysUntil,
        label: `${prefix} Due In ${daysUntil} ${unit}`,
      });
    } else if (daysUntil === 0) {
      flags.push({week: w.week, state: 'due', tone: 'danger', dueDate, daysUntil, label: `${prefix} Due`});
    } else {
      flags.push({week: w.week, state: 'missed', tone: 'danger', dueDate, daysUntil, label: `${prefix} Missed`});
    }
  }
  return flags;
}

// The one badge a card shows (Q15): danger beats warn; on a tie, the earlier week.
export function worstBroilerWeighInFlag(
  batch: BroilerWeighInBatch | null | undefined,
  todayIso: string,
): BroilerWeighInFlag | null {
  const flags = broilerWeighInFlags(batch, todayIso);
  return flags.find((f) => f.tone === 'danger') || flags[0] || null;
}
